'use client'

import { Link } from '@/i18n/navigation'
import { trackMarketingFeatureNavClicked, trackMarketingFeatureViewed } from '@/lib/analytics'
import { useEffect, useRef, useState } from 'react'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { ArrowRight } from '@/components/ui/icons'
import { SectionHeading, sectionInner, sectionShellAlt } from './SectionHeading'
import { MartiniWordmark } from '@/components/marketing/MartiniWordmark'
import { Frame } from './ProductionShared'
import { ProductionModels } from './ProductionModels'
import { ProductionCamera } from './ProductionCamera'
import { ProductionPerformance } from './ProductionPerformance'
import { ProductionLineage } from './ProductionLineage'
import { ProductionTimeline } from './ProductionTimeline'
import { ProductionUsage } from './ProductionUsage'
import { Check } from '@/components/ui/icons'

const logo = (name: string) => `/marketing/logos-mono/${name}.svg`
const EDITORS = [
  { name: 'Premiere Pro', src: logo('adobepremierepro') },
  { name: 'DaVinci Resolve', src: logo('davinciresolve') },
  { name: 'Final Cut Pro', src: logo('finalcutpro') },
  { name: 'CapCut', src: logo('capcut') },
]

// The export, moving: an XML file hops from the sequence to the editors, landing on each in turn.
const XML_HOP_SECONDS = 2.6
const XML_CSS = `
@keyframes xml-hop {
  0% { transform: translate(-34px, 0); opacity: 0 }
  15% { opacity: 1 }
  50% { transform: translate(0, -16px) }
  85% { opacity: 1 }
  100% { transform: translate(34px, 0); opacity: 0 }
}
@keyframes xml-land {
  0% { border-color: rgba(255,255,255,0.45); background-color: rgba(255,255,255,0.08) }
  8%, 100% { border-color: rgba(255,255,255,0.08); background-color: rgba(255,255,255,0.02) }
}
.xml-hop { animation: xml-hop ${XML_HOP_SECONDS}s ease-in-out infinite }
.xml-land { animation: xml-land ${XML_HOP_SECONDS * 4}s linear infinite }
@media (prefers-reduced-motion: reduce) { .xml-hop { display: none } .xml-land { animation: none } }
`

function XmlExport() {
  return (
    <div className="grid items-center gap-6 p-6 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:p-8">
      <style>{XML_CSS}</style>
      <div className="rounded-lg border border-white/[0.1] bg-white/[0.03]">
        <div className="flex items-center justify-between border-b border-white/[0.08] px-4 py-3">
          <span className="font-mono text-[14px] text-white/90">spot_02_v3.xml</span>
          <span className="text-[11px] tabular-nums text-white/40">14 clips · 00:30</span>
        </div>
        <pre className="overflow-hidden px-4 py-3 font-mono text-[11px] leading-relaxed text-white/40">
          {'<sequence name="spot_02">\n  <rate>24</rate>\n  <track type="video"> ×2\n  <track type="audio"> ×2'}
        </pre>
      </div>
      <div className="relative hidden h-12 w-20 items-center justify-center md:flex" aria-hidden>
        <ArrowRight className="h-5 w-5 text-white/20" />
        <span className="xml-hop absolute rounded-[5px] border border-white/40 bg-[#1c1c1c] px-1.5 py-1 font-mono text-[9px] font-semibold leading-none text-white">
          XML
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {EDITORS.map((editor, i) => (
          <div
            key={editor.name}
            // Each editor lights up as the file lands on it: one per hop, four hops to a cycle.
            style={{ animationDelay: `${(i + 0.9) * XML_HOP_SECONDS}s` }}
            className="xml-land flex items-center gap-3.5 rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-3.5"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/[0.06]">
              {/* eslint-disable-next-line @next/next/no-img-element -- static brand mark */}
              <img src={editor.src} alt="" className="h-6 w-6 object-contain" />
            </span>
            <span className="text-[14px] leading-tight text-white/80">{editor.name}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function NewTag() {
  return (
    <span className="ms-2 inline-block rounded-full bg-white px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase leading-none tracking-wide text-black">
      New
    </span>
  )
}

// Who connects, and how: the agents people already use on one side, more of them and a
// team's own tools on the other, each wired into Martini through one of its three doors.
// Exported for the homepage hero, which flanks the app window with the same tiles and wires.
export type Agent = { name: string; src?: string; via: 'MCP' | 'CLI' | 'API' }
export const AGENTS_LEFT: Agent[] = [
  { name: 'Claude', src: logo('claude'), via: 'MCP' },
  { name: 'Claude Code', src: logo('claudecode'), via: 'CLI' },
  { name: 'Codex', src: logo('codex'), via: 'CLI' },
]
export const AGENTS_RIGHT: Agent[] = [
  { name: 'Cursor', src: logo('cursor'), via: 'MCP' },
  { name: 'Gemini', src: logo('gemini'), via: 'MCP' },
  { name: 'Your own tools', via: 'API' },
]

// One clock for the whole diagram: a tile lights as its pulse leaves, the pulse runs down the
// wire with a fading tail, and the door on the wire lights as the pulse passes through it.
const AGENT_CYCLE_SECONDS = 4.8
// Staggered, and started mid-flight (negative) so the diagram is never empty.
const agentDelay = (i: number, flipped: boolean, lag = 0) => `${(i + (flipped ? 1.5 : 0)) * -1.6 + lag}s`
// The pulse is three stacked dashes that share a head: [length, opacity].
const PULSE_LAYERS = [
  [22, 0.12],
  [12, 0.3],
  [4, 1],
]
export const AGENT_CSS = `
@keyframes agent-pulse { from { stroke-dashoffset: 100 } to { stroke-dashoffset: 0 } }
.agent-pulse { animation: agent-pulse ${AGENT_CYCLE_SECONDS}s linear infinite }
@keyframes agent-door {
  0%, 12%, 36%, 100% { color: rgba(255,255,255,0.55); border-color: rgba(255,255,255,0.14) }
  23% { color: #fff; border-color: rgba(255,255,255,0.8) }
}
.agent-door { animation: agent-door ${AGENT_CYCLE_SECONDS}s linear infinite }
@keyframes agent-tile {
  0%, 100% { border-color: rgba(255,255,255,0.4) }
  12%, 90% { border-color: rgba(255,255,255,0.1) }
}
.agent-tile { animation: agent-tile ${AGENT_CYCLE_SECONDS}s linear infinite }
@media (prefers-reduced-motion: reduce) { .agent-pulse { display: none } .agent-door, .agent-tile { animation: none } }
`

// `beatClass` swaps the ambient border animation for the caller's own (the hero lights a tile
// on its story's clock); `compact` is the narrower tile the hero's margins have room for.
export function AgentTile({
  agent,
  pill = false,
  delay,
  beatClass,
  compact = false,
}: {
  agent: Agent
  pill?: boolean
  delay?: string
  beatClass?: string
  compact?: boolean
}) {
  return (
    <div
      style={delay && !beatClass ? { animationDelay: delay } : undefined}
      className={`${beatClass ?? (delay ? 'agent-tile' : '')} flex items-center border border-white/[0.1] bg-[#161616] ${
        compact ? 'gap-2 rounded-lg px-2 py-1.5' : 'gap-3 rounded-xl px-3 py-2.5'
      }`}
    >
      <span
        className={`flex shrink-0 items-center justify-center rounded-lg bg-white/[0.07] font-mono font-semibold text-white ${
          compact ? 'h-7 w-7 text-[9px]' : 'h-9 w-9 text-[11px]'
        }`}
      >
        {agent.src ? (
          // eslint-disable-next-line @next/next/no-img-element -- static brand mark
          <img src={agent.src} alt="" className={`object-contain ${compact ? 'h-4 w-4' : 'h-5 w-5'}`} />
        ) : (
          '</>'
        )}
      </span>
      <span className={`min-w-0 flex-1 leading-tight text-white/85 ${compact ? 'text-[11px]' : 'text-[13px]'}`}>
        {agent.name}
      </span>
      {pill && <span className="font-mono text-[10px] text-white/45">{agent.via}</span>}
    </div>
  )
}

// A wire's pulse and door taken off the shared clock: the caller's classes animate them
// instead, `lag` keeps the pulse's three layers sharing a head as `agentDelay` does, and
// `outClass` adds a second, single-dash pulse the caller runs the other way (a result going
// back out to the tile).
export type WireBeat = { pulseClass: string; doorClass: string; lag: (length: number) => string; outClass?: string }

// One side's three wires. Each starts at its tile and ends on Martini, so the pulse always
// travels inward; `flip` mirrors the drawing for the right-hand side.
export function AgentWires({
  agents,
  flip = false,
  beats,
}: {
  agents: Agent[]
  flip?: boolean
  beats?: (WireBeat | undefined)[]
}) {
  const ys = [16.67, 50, 83.33]
  const ends = [38, 50, 62]
  return (
    <div className="relative">
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className={`absolute inset-0 h-full w-full ${flip ? '-scale-x-100' : ''}`}
        aria-hidden
      >
        {ys.map((y, i) => {
          const d = `M0,${y} C55,${y} 45,${ends[i]} 100,${ends[i]}`
          const beat = beats?.[i]
          return (
            <g key={y} fill="none" vectorEffect="non-scaling-stroke">
              <path d={d} stroke="rgba(255,255,255,0.14)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              {PULSE_LAYERS.map(([length, opacity]) => (
                <path
                  key={length}
                  d={d}
                  pathLength={100}
                  stroke="white"
                  strokeOpacity={opacity}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray={`${length} ${100 - length}`}
                  vectorEffect="non-scaling-stroke"
                  className={beat ? beat.pulseClass : 'agent-pulse'}
                  // A longer layer runs slightly late, by its extra length, so every layer ends at the same head.
                  style={{
                    animationDelay: beat
                      ? beat.lag(length)
                      : agentDelay(i, flip, ((length - 4) / 100) * AGENT_CYCLE_SECONDS),
                  }}
                />
              ))}
              {beat?.outClass && (
                <path
                  d={d}
                  pathLength={100}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="5 95"
                  vectorEffect="non-scaling-stroke"
                  className={beat.outClass}
                />
              )}
            </g>
          )
        })}
      </svg>
      {agents.map((agent, i) => (
        <span
          key={agent.name}
          style={{
            top: `${ys[i] + (ends[i] - ys[i]) * 0.12}%`,
            [flip ? 'right' : 'left']: '22%',
            animationDelay: beats?.[i] ? undefined : agentDelay(i, flip),
          }}
          className={`${beats?.[i]?.doorClass ?? 'agent-door'} absolute -translate-y-1/2 rounded-md border border-white/[0.14] bg-[#0e0e0e] px-1.5 py-0.5 font-mono text-[10px] leading-none text-white/60`}
        >
          {agent.via}
        </span>
      ))}
    </div>
  )
}

// Martini is the one inverted box in the diagram, so the eye lands on it first. In the wired
// layout the caption hangs below the box, out of flow, so the box stays centred on the wires.
function MartiniHub() {
  return (
    <div className="relative self-center">
      <div className="flex items-center justify-center rounded-2xl bg-white px-5 py-8">
        <MartiniWordmark className="h-8 w-auto fill-black" />
      </div>
      <p className="mt-3 text-center text-[12px] leading-snug text-white/50 @[640px]:absolute @[640px]:left-1/2 @[640px]:top-full @[640px]:-translate-x-1/2 @[640px]:whitespace-nowrap">
        One project for people and agents
      </p>
    </div>
  )
}

export function InternalTools() {
  return (
    <div className="@container p-6 md:p-8">
      <style>{AGENT_CSS}</style>
      {/* With room (the frame itself is at least 640px wide): Martini in the middle, three agents wired in from each side. */}
      <div className="hidden h-[280px] grid-cols-[168px_minmax(0,1fr)_176px_minmax(0,1fr)_168px] @[640px]:grid">
        <div className="grid grid-rows-3 items-center">
          {AGENTS_LEFT.map((a, i) => (
            <AgentTile key={a.name} agent={a} delay={agentDelay(i, false)} />
          ))}
        </div>
        <AgentWires agents={AGENTS_LEFT} />
        <MartiniHub />
        <AgentWires agents={AGENTS_RIGHT} flip />
        <div className="grid grid-rows-3 items-center">
          {AGENTS_RIGHT.map((a, i) => (
            <AgentTile key={a.name} agent={a} delay={agentDelay(i, true)} />
          ))}
        </div>
      </div>
      {/* Narrower: no room for wires, so each tile carries its own door. */}
      <div className="grid gap-2 @[640px]:hidden">
        <MartiniHub />
        <div className="grid grid-cols-2 gap-2">
          {[...AGENTS_LEFT, ...AGENTS_RIGHT].map(a => (
            <AgentTile key={a.name} agent={a} pill />
          ))}
        </div>
      </div>
    </div>
  )
}

// The order is the production: generate, direct, arrange, track, cut, deliver, account.
const FEATURES = [
  {
    id: 'models',
    step: 'Generate',
    title: 'Generate',
    outcome: 'Every model worth using, in one place.',
    body: 'Generate video, images, audio and motion graphics, and pick the best model for each shot. No more juggling tabs and subscriptions.',
    visual: <ProductionModels />,
  },
  {
    id: 'camera',
    step: 'Direct',
    title: '3D camera control',
    outcome: 'Get the exact shot, not a lucky one.',
    body: 'Set the camera’s position, lens and movement in 3D.',
    visual: <ProductionCamera />,
  },
  {
    id: 'performance',
    step: 'Arrange',
    title: 'High-performance canvas',
    outcome: 'Fast, even with 1,000 videos on screen.',
    body: 'Scrub, drag and reorganize a whole production without a stutter.',
    visual: <ProductionPerformance />,
  },
  {
    id: 'lineage',
    step: 'Track',
    title: 'Lineage of every shot',
    outcome: 'Know how every shot was made.',
    body: 'Every prompt, model and reference image, remembered from start to finish.',
    visual: <ProductionLineage />,
    pad: true,
  },
  {
    id: 'timeline',
    step: 'Cut',
    title: 'Multiplayer timeline',
    outcome: 'Edit together, in real time.',
    body: 'People and agents cutting on one sequence.',
    visual: <ProductionTimeline />,
  },
  {
    id: 'export',
    step: 'Deliver',
    title: 'XML export',
    outcome: 'Finish in the editor you already use.',
    body: 'Export XML to Premiere Pro, DaVinci Resolve, Final Cut Pro and CapCut.',
    visual: <XmlExport />,
  },
  {
    id: 'internal-tools',
    step: 'Connect',
    title: 'Martini Bar',
    outcome: 'Keep building your own tools. Let Martini run the bar.',
    isNew: true,
    body: (
      <>
        Connect your studio’s tools and agents over the API, CLI or MCP, and they share the canvas, timeline, provenance
        and cost tracking.{' '}
        <Link
          href="/bar"
          data-cta="home_martini_bar"
          className="text-white/80 underline underline-offset-4 hover:text-white"
        >
          Meet Martini Bar
        </Link>
      </>
    ),
    visual: <InternalTools />,
  },
  {
    id: 'usage',
    step: 'Account',
    title: 'Spend analytics',
    outcome: 'Know where the budget went.',
    body: 'Spend by person, project and model. Agents included.',
    visual: <ProductionUsage />,
  },
  {
    id: 'keys',
    step: 'Account',
    title: 'Bring your own bottle',
    outcome: 'Generate on your own fal account, at your fal rates.',
    isNew: true,
    body: 'Plug in your studio’s fal key and generate through Martini. Martini starts free.',
    visual: <ByokKeys />,
  },
]

// The connected-key setting, plus where that spend shows up next to olives.
// What this month's generation cost on the studio's own fal account, model by model.
const FAL_SPEND = [
  { model: 'Seedance 2.5', usd: 1840 },
  { model: 'Kling O3', usd: 1212 },
  { model: 'Veo 3.1', usd: 863 },
]

// One quiet panel: the connected key, then the month's fal spend broken down by model. Hairlines
// instead of boxes inside the box.
export function ByokKeys() {
  const total = FAL_SPEND.reduce((sum, r) => sum + r.usd, 0)
  const usd = (n: number) => `$${n.toLocaleString('en-US')}`
  return (
    <Frame className="p-6 md:p-8">
      <div className="flex items-center gap-3 border-b border-white/[0.08] pb-5">
        {/* eslint-disable-next-line @next/next/no-img-element -- static brand mark */}
        <img src="/marketing/logos-mono/fal.svg" alt="" className="h-5 w-5" />
        <span className="text-[15px] font-medium text-white">fal</span>
        <span className="flex-1 truncate font-mono text-[13px] tracking-wider text-white/40">fal_••••••••3f9a</span>
        <span className="inline-flex items-center gap-1.5 text-[13px] text-white/70">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Connected
        </span>
      </div>
      <div className="mt-6 flex items-baseline justify-between gap-4">
        <p className="text-[13px] text-white/45">Billed to your fal account this month</p>
        <p className="text-[28px] font-semibold tabular-nums tracking-tight text-white">{usd(total)}</p>
      </div>
      <ul className="mt-4 space-y-3">
        {FAL_SPEND.map(r => (
          <li key={r.model} className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-4 text-[13px]">
            <span className="truncate text-white/70">{r.model}</span>
            <span className="h-1 overflow-hidden rounded-full bg-white/[0.06]">
              <span className="block h-full rounded-full bg-white/60" style={{ width: `${(r.usd / total) * 100}%` }} />
            </span>
            <span className="tabular-nums text-white/70">{usd(r.usd)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-6 border-t border-white/[0.08] pt-4 text-[13px] text-white/40">
        0 olives used · every take still logged with who made it and on which model
      </p>
    </Frame>
  )
}

// Desktop: the heading and a feature list stay pinned on the left; the list lights up the one in view.
// Mobile: the heading sits on top and the list is hidden.
// `ids` narrows the section to a few features, in the order given (the clear layout leads with the core four);
// the default is every feature, in production order.
export function ProductionSection({
  ids,
  lead = 'Built for end‑to‑end generative video production.',
  rest = 'Generate, direct, cut and deliver in one place.',
}: {
  ids?: string[]
  lead?: string
  rest?: string
}) {
  const sectionRef = useScrollAnimation<HTMLElement>()
  const listRef = useRef<HTMLDivElement>(null)
  const features = ids ? ids.flatMap(id => FEATURES.filter(f => f.id === id)) : FEATURES
  const [active, setActive] = useState(features[0].id)

  useEffect(() => {
    const rows = listRef.current?.querySelectorAll<HTMLElement>('[data-feature]')
    if (!rows?.length) return
    // The row crossing the upper-middle band of the viewport is the active one.
    const io = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          if (!e.isIntersecting) continue
          const feature = e.target.getAttribute('data-feature')!
          setActive(feature)
          trackMarketingFeatureViewed({ feature })
        }
      },
      { rootMargin: '-30% 0px -60% 0px' },
    )
    rows.forEach(r => io.observe(r))
    return () => io.disconnect()
  }, [])

  return (
    <section ref={sectionRef} id="production" className={`animate-on-scroll ${sectionShellAlt}`}>
      <div className={sectionInner}>
        <div className="lg:grid lg:grid-cols-[340px_minmax(0,1fr)] lg:gap-20">
          {/* The heading and the feature list travel together, so the section keeps its title in view. */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            <SectionHeading lead={lead} rest={rest} stacked className="lg:text-[40px]" />
            <nav aria-label="Features" className="mt-12 hidden lg:block">
              <ol className="border-l border-white/[0.1]">
                {features.map(f => {
                  const on = f.id === active
                  return (
                    <li key={f.id} className="-ml-px">
                      <a
                        href={`#${f.id}`}
                        aria-current={on ? 'true' : undefined}
                        onClick={() => trackMarketingFeatureNavClicked({ feature: f.id })}
                        className={`block border-l-2 py-2 pl-5 text-[15px] leading-snug transition-colors duration-200 ${
                          on ? 'border-white text-white' : 'border-transparent text-white/40 hover:text-white/75'
                        }`}
                      >
                        {f.title}
                        {'isNew' in f && <NewTag />}
                      </a>
                    </li>
                  )
                })}
              </ol>
            </nav>
          </div>

          <div ref={listRef} className="mt-16 md:mt-20 lg:mt-0">
            {features.map(f => (
              <article
                key={f.id}
                id={f.id}
                data-feature={f.id}
                className="scroll-mt-32 border-t border-white/[0.07] py-12 first:border-t-0 first:pt-0 md:py-16"
              >
                <p className="text-[13px] font-medium text-white/50">
                  {f.title}
                  {'isNew' in f && <NewTag />}
                </p>
                <h3 className="mt-2 text-[26px] font-medium leading-tight tracking-[-0.02em] text-white md:text-[30px]">
                  {f.outcome}
                </h3>
                <p className="mt-3 max-w-[52ch] text-[16px] leading-relaxed text-white/45">{f.body}</p>
                <Frame className={`mt-8 ${f.pad ? 'p-4 md:p-6' : ''}`}>{f.visual}</Frame>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// How far each demo is scaled down inside its bento card on desktop, where the demos were
// drawn for a column about twice as wide. Phones show them at full size, one card per row.
const BENTO_ZOOM: Record<string, number> = {
  performance: 0.6,
  lineage: 0.6,
  camera: 1,
  usage: 0.6,
  keys: 0.75,
}

// The rest of the product as a bento grid: every demo visible without a click, the first
// two wide and the rest narrow.
// ponytail: layout="list" (?more=list) keeps the earlier click-to-open list for comparison;
// delete that branch once the founder picks.
export function MoreFeaturesSection({ ids, layout = 'bento' }: { ids: string[]; layout?: 'bento' | 'list' }) {
  const sectionRef = useScrollAnimation<HTMLElement>()
  const features = ids.flatMap(id => FEATURES.filter(f => f.id === id))
  if (layout === 'bento') {
    return (
      <section ref={sectionRef} id="more-features" className={`animate-on-scroll ${sectionShellAlt}`}>
        <div className={sectionInner}>
          <SectionHeading lead="Built for performance and scale." />
          <div className="mt-14 grid gap-4 lg:grid-cols-6">
            {features.map((f, i) => (
              <article
                key={f.id}
                id={f.id}
                className={`flex scroll-mt-32 flex-col overflow-hidden rounded-2xl border border-white/[0.09] bg-white/[0.02] ${
                  i < 2 ? 'lg:col-span-3' : 'lg:col-span-2'
                }`}
              >
                <div className="p-6 md:p-7">
                  <p className="text-[13px] font-medium text-white/50">
                    {f.title}
                    {'isNew' in f && <NewTag />}
                  </p>
                  <h3 className="mt-2 text-[20px] font-medium tracking-[-0.02em] text-white md:text-[22px]">
                    {f.outcome}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-white/45">{f.body}</p>
                </div>
                {/* The demo sits on the bottom edge of the card and runs off its right side. */}
                <div
                  className="relative mt-auto overflow-hidden ps-6 md:ps-7"
                  onPointerDown={() => trackMarketingFeatureViewed({ feature: f.id })}
                >
                  <div
                    className="lg:[zoom:var(--bento-zoom)]"
                    style={{ '--bento-zoom': BENTO_ZOOM[f.id] } as React.CSSProperties}
                  >
                    <Frame
                      className={`rounded-b-none rounded-e-none border-b-0 border-e-0 ${f.pad ? 'p-4 md:p-6' : ''}`}
                    >
                      {f.visual}
                    </Frame>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    )
  }
  return (
    <section ref={sectionRef} id="more-features" className={`animate-on-scroll ${sectionShellAlt}`}>
      <div className={sectionInner}>
        <SectionHeading lead="Built for performance and scale." />
        <div className="mt-14 border-t border-white/[0.1]">
          {features.map(f => (
            <details
              key={f.id}
              id={f.id}
              className="group/more scroll-mt-32 border-b border-white/[0.1]"
              onToggle={e => {
                if (e.currentTarget.open) trackMarketingFeatureViewed({ feature: f.id })
              }}
            >
              <summary className="grid cursor-pointer list-none items-baseline gap-x-10 gap-y-2 py-7 md:grid-cols-[minmax(0,4fr)_minmax(0,7fr)_auto] [&::-webkit-details-marker]:hidden">
                <h3 className="text-[22px] font-medium tracking-[-0.02em] text-white md:text-[24px]">
                  {f.title}
                  {'isNew' in f && <NewTag />}
                </h3>
                <p className="text-[16px] leading-relaxed text-white/45">{f.body}</p>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[14px] text-white/60 transition-colors group-hover/more:text-white">
                  <span className="group-open/more:hidden">See it</span>
                  <span className="hidden group-open/more:inline">Hide</span>
                  <ArrowRight className="h-3.5 w-3.5 rotate-90 transition-transform duration-200 group-open/more:-rotate-90" />
                </span>
              </summary>
              <Frame className={`mb-10 ${f.pad ? 'p-4 md:p-6' : ''}`}>{f.visual}</Frame>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}
